import React, { Suspense, lazy, useState } from 'react';
import { Video, X } from 'lucide-react';

// A short intro video (Katia's Scene Edit) pinned to the top of the page. Shown on
// first visit; closing it hides it for good (persisted), but the "Watch intro" link
// below always brings it back. Plays through Mux Player (HLS + Mux Data), matching
// the rest of the app — not a raw MP4. Public playback, so no signing tokens.
const MuxPlayer = lazy(() => import('@mux/mux-player-react'));

const DISMISS_KEY = 'sceneEditVideoIntroDismissed';
// Public Mux asset (park scene edit). Poster frame pinned to 0:16 — the frame Joe picked.
const PLAYBACK_ID = 'gpwUpCCFKxC3Z8i01WuQfanRdCxRfSv4R55901452TWNA';

const readFlag = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const writeFlag = (key: string, value: string) => {
  try { localStorage.setItem(key, value); } catch { /* private window */ }
};

export const SceneEditVideoIntro: React.FC = () => {
  // Open on first visit; closed once the viewer has dismissed it before.
  const [open, setOpen] = useState<boolean>(() => readFlag(DISMISS_KEY) !== '1');

  const close = () => {
    writeFlag(DISMISS_KEY, '1'); // gone for good across reloads
    setOpen(false);
  };
  // Reopen for this view only; the persisted flag stays set so it won't nag next load.
  const reopen = () => setOpen(true);

  const ink = 'var(--hj-beige-ink)';
  const line = 'var(--hj-beige-line)';

  if (!open) {
    return (
      <button
        type="button"
        onClick={reopen}
        className="flex items-center gap-2"
        style={{
          marginBottom: 16, minHeight: 44, padding: '6px 2px', background: 'transparent',
          border: 0, color: ink, fontSize: 14, fontWeight: 600, cursor: 'pointer',
        }}
      >
        <Video size={18} strokeWidth={1.8} className="flex-shrink-0" />
        <span style={{ textDecoration: 'underline', textUnderlineOffset: 3 }}>Watch intro</span>
      </button>
    );
  }

  return (
    <section
      data-paper="beige"
      style={{
        background: 'var(--hj-beige)',
        border: `1px solid ${line}`,
        borderRadius: 'var(--hj-r-md)',
        color: ink,
        marginBottom: 16,
        padding: 14,
        position: 'relative',
      }}
    >
      <div className="flex items-center justify-between" style={{ marginBottom: 10, gap: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>See what Scene Edit can do</div>
        <button
          type="button"
          onClick={close}
          aria-label="Close intro"
          className="flex items-center justify-center flex-shrink-0"
          style={{ width: 32, height: 32, border: 0, borderRadius: 999, background: 'transparent', color: ink, cursor: 'pointer' }}
        >
          <X size={18} strokeWidth={2} />
        </button>
      </div>
      <Suspense fallback={<div style={{ width: '100%', aspectRatio: '16 / 9', borderRadius: 10, background: '#000' }} />}>
        <MuxPlayer
          playbackId={PLAYBACK_ID}
          streamType="on-demand"
          thumbnailTime={16}
          metadata={{ video_title: 'Scene Edit intro (Katia)' }}
          style={{ display: 'block', width: '100%', aspectRatio: '16 / 9', borderRadius: 10, overflow: 'hidden', background: '#000' }}
        />
      </Suspense>
    </section>
  );
};
